import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../ApplicationState';
import { setSelectedClassroom } from '../GlobalFunctions';
import { ClassroomType } from '../../peekapak-types/DataProtocolTypes';
import ClassroomStudentLink from './ClassroomStudentLink';
import ClassroomCardButton from './ClassroomCardButton';
import { Icon } from '@iconify/react';
import bytesizeArchive from '@iconify-icons/bytesize/archive';
import gearIcon from '../images/pkp-gear.svg';
import cx from 'classnames';
import styles from '../SCSS/ClassroomCard.module.scss';

interface Props {
  data: ClassroomType;
  animate?: boolean;
  onSettings: (
    classroom: ClassroomType,
    classroomIndex: number,
    event: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => void;
  onUnarchive?: (classroom: ClassroomType) => void;
  isSelectedClassroom: boolean;
}

const getGradeDescription = (language: string, grade: number | string) => {
  let gradeForDisplay = grade;
  if (grade === -1) {
    gradeForDisplay = 'PK';
  } else if (grade === 0) {
    gradeForDisplay = 'K';
  } else {
    gradeForDisplay = grade;
  }
  return language === 'es' ? `spanish grade ${gradeForDisplay}` : `grade ${gradeForDisplay}`;
};

export const ClassroomCard = (props: Props): JSX.Element => {
  const { data, animate, onUnarchive, onSettings, isSelectedClassroom } = props;
  const { className, isArchived, language, grade, listOfStudentProxies } = data;

  const history = useHistory();

  const classrooms = useSelector((state: RootState) => {
    return state.classrooms.classrooms;
  });

  const classroomName = className;
  const gradeDescription = getGradeDescription(language, grade);

  const getClassroomCardButtonText = () => {
    if (isSelectedClassroom && !isArchived) {
      return 'Resume';
    } else if (isArchived) {
      return 'Unarchive';
    } else {
      return 'Teach';
    }
  };

  const classroomIndex = classrooms.findIndex((classroom) => classroom.className === data.className);

  const handleClickClassroomCardButton = () => {
    if (isSelectedClassroom && !isArchived) {
      setSelectedClassroom(classroomIndex);
      history.push('/library');
    } else if (isArchived) {
      onUnarchive && onUnarchive(data);
    } else {
      setSelectedClassroom(classroomIndex);
      history.push('/library');
    }
  };

  return (
    <div
      className={cx(styles.Card, styles.cardContainer, {
        [`${styles.creationFlip}`]: animate,
        [`${styles.archived}`]: isArchived,
      })}
    >
      <div>
        <div>
          <div className={styles.upperHalf}>
            <div className={styles.settingsButton}>
              {!isArchived && (
                <button
                  type='button'
                  onClick={(event) => {
                    onSettings(data, classroomIndex, event);
                  }}
                  disabled={isArchived}
                >
                  <img alt='Gear' src={gearIcon} />
                </button>
              )}
            </div>
            <div className={styles.upperHalfContents}>
              <div className={styles.gradeDescription}>{gradeDescription}</div>
              <div className={styles.classroomName}>
                {isArchived && (
                  <Icon icon={bytesizeArchive} width='1em' style={{ marginRight: '0.25em', marginBottom: '0.2em' }} />
                )}
                {classroomName}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div>
        <div>
          <div className={styles.lowerHalf}>
            {!isArchived && (
              <ClassroomStudentLink
                classroomName={className}
                classroomIndex={classroomIndex}
                students={listOfStudentProxies}
              />
            )}
            <ClassroomCardButton
              buttonText={getClassroomCardButtonText()}
              handleClick={handleClickClassroomCardButton}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
